import React from 'react';
import { Box, Typography } from '@mui/material';
import MessageItem from './MessageItem';
import { ChatMessage } from '../types/chat';

// Enhanced message type with audio properties
interface EnhancedChatMessage extends ChatMessage {
  isAudioEnabled?: boolean;
  isCurrentlyPlaying?: boolean;
  isApiLoading?: boolean; // Changed from isAudioLoading to isApiLoading
  hasCachedAudio?: boolean;
  audioProgress?: number;
  onPlayAudio?: (messageId: string, text: string) => void;
  onStopAudio?: () => void;
}

interface MessageListProps {
  messages: EnhancedChatMessage[];
  sessionId: string | null;
  audioEnabled?: boolean;
  currentlyPlayingId?: string | null;
  apiLoadingId?: string | null; // Changed from isAudioLoading to apiLoadingId
  onPlayAudio?: (messageId: string, text: string) => void;
  onStopAudio?: () => void;
  isOverallTyping: boolean;
  // audioLoadingMessageId?: string | null;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  sessionId,
  audioEnabled = false,
  currentlyPlayingId = null,
  apiLoadingId = null, // Changed from isAudioLoading to apiLoadingId
  onPlayAudio,
  onStopAudio,
  isOverallTyping,
}) => {
  if (!messages || messages.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center py-8">
        <Typography variant="body2" color="textSecondary" className="text-center text-sm">
          No messages yet. Start a conversation!
        </Typography>
      </Box>
    );
  }

  const filteredMessages = messages.filter(message => {
    // Keep user messages
    if (message.role === 'user') return true;

    // Keep AI streaming messages
    if (message.isStreaming) return true;

    // Filter out empty AI messages
    if (message.role === 'ai' && (!message.text || message.text.trim() === '')) {
      return false;
    }

    return true;
  });

  return (
    <div className="messages-container">
      {messages.map((message, index) => {
        // Skip rendering temporary typing indicators that are not the last one
        if (message.isStreaming && index !== messages.length - 1) {
          return null;
        }

        const messageId = message.id || `msg-${index}`;
        const isThisMessageLoadingAudio = apiLoadingId === messageId;

        if (message.isStreaming && index !== filteredMessages.length - 1) {
          return null;
        }

        return (
          <MessageItem
            key={messageId}
            message={message}
            sessionId={sessionId}
            audioEnabled={audioEnabled}
            // isApiLoading={isThisMessageLoadingAudio}
            currentlyPlayingId={currentlyPlayingId}
            isApiLoading={isThisMessageLoadingAudio} // Pass boolean based on ID match
            hasCachedAudio={message.hasCachedAudio}
            audioProgress={message.audioProgress}
            onPlayAudio={onPlayAudio}
            onStopAudio={onStopAudio}
            isGlobalTyping={isOverallTyping}
          />
        );
      })}
    </div>
  );
};

export default MessageList;
